import { ChartPie } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  endOfWeekISO,
  formatDayHeading,
  formatMonth,
  formatMonthShort,
  formatRange,
  formatShortDate,
  monthRange,
  rangeForPreset,
  RANGE_PRESETS,
  type DateRange,
  type RangePreset,
} from '@core/dates';

import { BarChart, CategoryBars } from '@/components/charts';
import { Chip, EmptyState } from '@/components/ui';
import { bucketFor, categoryTotals, summarize, trailingMonths, trend } from '@/lib/analytics';
import { useApp, useMoney } from '@/lib/hooks';
import { liveExpenses } from '@/lib/merge';

export function Insights() {
  const { data } = useApp();
  const { format, formatWhole } = useMoney();
  const navigate = useNavigate();
  const [preset, setPreset] = useState<RangePreset | 'custom'>('thisMonth');
  const [custom, setCustom] = useState<DateRange>(() => rangeForPreset('thisMonth', new Date()));
  const [trendCat, setTrendCat] = useState<string | null>(null);
  const now = new Date();
  const range = preset === 'custom' ? custom : rangeForPreset(preset, now);

  const v = useMemo(() => {
    const expenses = liveExpenses(data);
    const s = summarize(expenses, range);
    const bucket = bucketFor(range);
    const cats = categoryTotals(expenses, data.categories, range);
    const months = trailingMonths(range);
    const catId = trendCat ?? cats[0]?.category.id ?? null;
    return {
      s,
      bucket,
      cats,
      points: trend(expenses, range, bucket),
      monthly: trend(expenses, months, 'month'),
      catId,
      catMonthly: catId ? trend(expenses.filter((e) => e.categoryId === catId), months, 'month') : [],
    };
  }, [data, range.from, range.to, trendCat]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (params: Record<string, string>) => navigate(`/expenses?${new URLSearchParams(params)}`);
  const bucketRange = (key: string): DateRange => {
    const r = v.bucket === 'day' ? { from: key, to: key } : v.bucket === 'week' ? { from: key, to: endOfWeekISO(key) } : monthRange(key);
    return { from: r.from < range.from ? range.from : r.from, to: r.to > range.to ? range.to : r.to };
  };
  const monthBars = (points: { key: string; totalMinor: number }[]) =>
    points.map((p) => ({ key: p.key, value: p.totalMinor, label: formatMonthShort(p.key), tooltip: formatMonth(p.key) }));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Insights</h1>
          <div className="muted" style={{ marginTop: 4 }}>{formatRange(range)}</div>
        </div>
      </div>
      <div className="chips" style={{ marginBottom: 16 }}>
        {RANGE_PRESETS.map((p) => (
          <Chip key={p.key} on={preset === p.key} onClick={() => setPreset(p.key)}>{p.label}</Chip>
        ))}
        <Chip on={preset === 'custom'} onClick={() => setPreset('custom')}>Custom</Chip>
        {preset === 'custom' && (
          <span className="row">
            <input className="input" type="date" value={custom.from} max={custom.to} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} style={{ width: 160 }} aria-label="From" />
            <span className="muted">to</span>
            <input className="input" type="date" value={custom.to} min={custom.from} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} style={{ width: 160 }} aria-label="To" />
          </span>
        )}
      </div>

      {v.s.count === 0 ? (
        <div className="card">
          <EmptyState icon={ChartPie} title="Nothing to analyse here" text={`No expenses between ${formatRange(range)}. Try a longer range.`} />
        </div>
      ) : (
        <div className="grid">
          <section className="card hero">
            <div className="muted">Total spent</div>
            <div className="hero-amount num">{format(v.s.totalMinor)}</div>
            <div className="muted">{v.s.count} expenses · {formatRange(range)}</div>
            <div className="metrics">
              <div className="metric"><div className="label">Avg / day</div><div className="value num">{formatWhole(v.s.avgPerDayMinor)}</div></div>
              <div className="metric">
                <div className="label">Largest</div>
                <div className="value num">{v.s.largest ? format(v.s.largest.amountMinor) : '—'}</div>
                <div className="faint ellipsis" style={{ fontSize: 12 }}>{v.s.largest?.title}</div>
              </div>
              <div className="metric">
                <div className="label">Top category</div>
                <div className="value">{v.cats[0]?.category.name ?? '—'}</div>
                {v.cats[0] && <div className="faint" style={{ fontSize: 12 }}>{Math.round((v.cats[0].totalMinor / v.s.totalMinor) * 100)}% of spend</div>}
              </div>
            </div>
          </section>

          <div className="grid grid-2">
            <section className="card">
              <div className="card-head"><h2>Spending trend</h2><span className="muted">Per {v.bucket}</span></div>
              <BarChart
                key={`${range.from}${range.to}`}
                data={v.points.map((p) => ({
                  key: p.key,
                  value: p.totalMinor,
                  label: v.bucket === 'day' ? String(Number(p.key.slice(8))) : v.bucket === 'week' ? formatShortDate(p.key) : formatMonthShort(p.key),
                  tooltip: v.bucket === 'day' ? formatDayHeading(p.key, now) : v.bucket === 'week' ? `Week of ${formatShortDate(p.key)}` : formatMonth(p.key),
                }))}
                format={(x) => format(x)}
                formatAxis={(x) => format(x, true)}
                onSelect={(b) => open(bucketRange(b.key))}
                label="Spending trend"
              />
            </section>
            <section className="card">
              <div className="card-head"><h2>By category</h2></div>
              <CategoryBars items={v.cats.map((c) => ({ category: c.category, value: c.totalMinor }))} format={(x) => format(x)} onSelect={(c) => open({ categoryId: c.id, from: range.from, to: range.to })} />
            </section>
          </div>

          <div className="grid grid-2">
            <section className="card">
              <div className="card-head"><h2>Monthly comparison</h2><span className="muted">Last 6 months</span></div>
              <BarChart data={monthBars(v.monthly)} format={(x) => format(x)} formatAxis={(x) => format(x, true)} highlight={v.monthly.at(-1)?.key} onSelect={(b) => open(monthRange(b.key))} label="Last 6 months" />
            </section>
            <section className="card">
              <div className="card-head"><h2>Category trend</h2></div>
              <div className="chips" style={{ marginBottom: 12 }}>
                {v.cats.map((c) => (
                  <Chip key={c.category.id} on={c.category.id === v.catId} onClick={() => setTrendCat(c.category.id)}>{c.category.name}</Chip>
                ))}
              </div>
              <BarChart key={v.catId ?? ''} data={monthBars(v.catMonthly)} format={(x) => format(x)} formatAxis={(x) => format(x, true)} highlight={v.catMonthly.at(-1)?.key} label="Monthly spending for the selected category" />
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
